import { IDataStore } from '@/services/dataStore/IDataStore'
import { retryTimeouts } from '@/services/dataStore/retryTimeouts'
import useDataStore from '@/services/dataStore/useDataStore'
import { useReferencesStore } from '@/store/references'
import {
  BaseDataLayerReferenceSchema,
  IBaseDataLayerReference
} from '@lifk/types'
import useSWRV from 'swrv'
import { computed, ref, watch } from 'vue'

interface GetDataLayerSWRVKey {
  name: string
  dataLayerKey?: string
}
const currentRouteSWRVKey = ref<GetDataLayerSWRVKey>({
  name: 'dataLayer'
})

const useGetDataLayer = () => {
  const {
    dataStoreSWRVKey,
    dataStore,
    handleDataStoreError,
    disableValidations
  } = useDataStore()
  const referencesStore = useReferencesStore()

  const dataLayerKey = computed(() =>
    referencesStore.selectedContentType === 'dataLayer'
      ? referencesStore.queryValues?.contentKey
      : undefined
  )
  currentRouteSWRVKey.value.dataLayerKey = dataLayerKey.value

  async function getDataLayer(
    dataStore: IDataStore,
    dataLayerKey: string
  ): Promise<IBaseDataLayerReference | undefined> {
    try {
      const response = await retryTimeouts(() =>
        dataStore.getDataLayer(dataLayerKey)
      )

      if (disableValidations.value) return response
      return BaseDataLayerReferenceSchema.parse(response)
    } catch (e) {
      handleDataStoreError(e, 'dataLayer')
      throw e
    }
  }

  const result = useSWRV(
    () =>
      `/${currentRouteSWRVKey.value.name}/${dataStoreSWRVKey.value.perspective}/${currentRouteSWRVKey.value.dataLayerKey}`,
    async () => {
      if (currentRouteSWRVKey.value.dataLayerKey) {
        return await getDataLayer(
          dataStore.value,
          currentRouteSWRVKey.value.dataLayerKey
        )
      } else {
        return undefined
      }
    },
    {
      // 30s for adapters to prevent too many API calls to paid CMS's
      dedupingInterval: 30000,
      shouldRetryOnError: false
    }
  )

  watch(dataLayerKey, () => {
    currentRouteSWRVKey.value.dataLayerKey = dataLayerKey.value
  })

  return result
}

export { useGetDataLayer }
